import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import Icon from '../../components/common/Icon';
import { icons } from '../../config/icons';
import api from '../../api/api';
import { useNotifier } from '../../context/NotificationContext';
import { useModal } from '../../context/ModalContext';
import { FiPlay, FiSquare } from 'react-icons/fi';
import { useQuery } from '@tanstack/react-query';

const EmployeeDashboard = () => {
    const { user } = useAuth();
    const { notify } = useNotifier();
    const { openPopup, closePopup } = useModal();
    const [loading, setLoading] = useState(false);
    const [currentTime, setCurrentTime] = useState(new Date());
    const [todayStatus, setTodayStatus] = useState(null);
    const [elapsedSeconds, setElapsedSeconds] = useState(0);

    const fetchWithToken = (url) => {
        const token = localStorage.getItem('token');
        return api.get(url, { headers: { Authorization: `Bearer ${token}` } }).then(res => res.data);
    };

    const { data: attResponse } = useQuery({ queryKey: ['employee-portal/attendance'], queryFn: () => fetchWithToken('/api/employee-portal/attendance') });
    const { data: assignResponse } = useQuery({ queryKey: ['employee-portal/assignments'], queryFn: () => fetchWithToken('/api/employee-portal/assignments') });
    
    // Calculate KPIs safely
    const attendanceData = Array.isArray(attResponse?.data) ? attResponse.data : [];
    const assignmentData = Array.isArray(assignResponse?.data) ? assignResponse.data : [];
    
    const pendingTimesheets = attendanceData.filter(att => att.status === 'Pending').length;
    const upcomingAssignments = assignmentData.filter(assign => assign.status === 'Active' || assign.status === 'Pending').length;
    const unreadMessages = 0; // Placeholder until messaging system is built

    const fetchTodayStatus = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await api.get('/api/employee-portal/today-status', { headers: { Authorization: `Bearer ${token}` } });
            if (res.data.success) {
                setTodayStatus(res.data.data);
            }
        } catch (error) {
            console.error('Error fetching today status:', error);
        }
    }, []);

    useEffect(() => {
        fetchTodayStatus();
    }, [fetchTodayStatus]);

    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentTime(new Date());

            if (todayStatus && todayStatus.clock_in && !todayStatus.clock_out) {
                const clockInTime = new Date(todayStatus.clock_in);
                const now = new Date();
                const diffSecs = Math.floor((now - clockInTime) / 1000);
                setElapsedSeconds(diffSecs < 0 ? 0 : diffSecs);
            }
        }, 1000);
        return () => clearInterval(timer);
    }, [todayStatus]);

    const isClockedIn = todayStatus && todayStatus.clock_in && !todayStatus.clock_out;
    const isClockedOut = todayStatus && todayStatus.clock_in && todayStatus.clock_out;

    const handleClockIn = async () => {
        openPopup({
            title: 'Start Shift',
            content: <p>Are you sure you want to start your shift now?</p>,
            actions: [
                { text: 'Cancel', className: 'btn', onClick: closePopup },
                {
                    text: 'Confirm', className: 'btn-primary', onClick: async () => {
                        closePopup();
                        setLoading(true);
                        try {
                            const token = localStorage.getItem('token');
                            const res = await api.post('/api/employee-portal/clock', { action: 'in' }, { headers: { Authorization: `Bearer ${token}` } });
                            if (res.data.success) {
                                notify({ title: 'Success', message: 'Successfully clocked in!', style: 'success' });
                                fetchTodayStatus();
                            }
                        } catch (error) {
                            notify({ title: 'Error', message: error.response?.data?.message || 'Failed to clock in.', style: 'error' });
                        } finally {
                            setLoading(false);
                        }
                    }
                }
            ]
        });
    };

    const handleClockOut = async () => {
        openPopup({
            title: 'End Shift',
            content: <p>Are you sure you want to end your shift? This action cannot be undone.</p>,
            style: 'warning',
            actions: [
                { text: 'Cancel', className: 'btn', onClick: closePopup },
                {
                    text: 'Confirm', className: 'btn-primary', onClick: async () => {
                        closePopup();
                        setLoading(true);
                        try {
                            const token = localStorage.getItem('token');
                            const res = await api.post('/api/employee-portal/clock', { action: 'out' }, { headers: { Authorization: `Bearer ${token}` } });
                            if (res.data.success) {
                                notify({ title: 'Success', message: 'Successfully clocked out!', style: 'success' });
                                fetchTodayStatus();
                            }
                        } catch (error) {
                            notify({ title: 'Error', message: error.response?.data?.message || 'Failed to clock out.', style: 'error' });
                        } finally {
                            setLoading(false);
                        }
                    }
                }
            ]
        });
    };

    const formatTime = (totalSeconds) => {
        const hrs = Math.floor(totalSeconds / 3600);
        const mins = Math.floor((totalSeconds % 3600) / 60);
        const secs = Math.floor(totalSeconds % 60);
        return `${String(hrs).padStart(2, '0')}:${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    };

    return (
        <div className="admin-pager">
            <header className="admin-pager-head">
                <div className='admin-pager-title'>
                    <span>
                        <h2>Welcome back, {user?.fn || 'Employee'}!</h2>
                        <p>Employee Portal Dashboard</p>
                    </span>
                </div>
                <div className='admin-pager-navs'>
                    <div className="td-badge light" style={{ fontSize: '14px', padding: '8px 12px' }}>
                        <Icon icon={icons.calendar} />
                        <span style={{ marginLeft: '8px' }}>
                            {currentTime.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric' })}
                        </span>
                    </div>
                </div>
            </header>

            <div className="admin-pager-body">

                {/* KPI Cards */}
                <div className="dash-kpi-grid">
                    <div className="pagelet">
                        <div className="pagelet-in dash-kpi">
                            <div className="dash-kpi-icon" style={{ backgroundColor: 'rgba(245, 158, 11, 0.15)', color: '#f59e0b' }}>
                                <Icon icon={icons.briefcase} />
                            </div>
                            <div>
                                <p className="dash-kpi-lbl">Assignments</p>
                                <p className="dash-kpi-val">{upcomingAssignments}</p>
                            </div>
                        </div>
                    </div>

                    <div className="pagelet">
                        <div className="pagelet-in dash-kpi">
                            <div className="dash-kpi-icon" style={{ backgroundColor: 'rgba(139, 92, 246, 0.15)', color: '#8b5cf6' }}>
                                <Icon icon={icons.fileText} />
                            </div>
                            <div>
                                <p className="dash-kpi-lbl">Pending Timesheets</p>
                                <p className="dash-kpi-val">{pendingTimesheets}</p>
                            </div>
                        </div>
                    </div>

                    <div className="pagelet">
                        <div className="pagelet-in dash-kpi">
                            <div className="dash-kpi-icon" style={{ backgroundColor: 'rgba(14, 165, 233, 0.15)', color: '#0ea5e9' }}>
                                <Icon icon={icons.bell} />
                            </div>
                            <div>
                                <p className="dash-kpi-lbl">Unread Messages</p>
                                <p className="dash-kpi-val">{unreadMessages}</p>
                            </div>
                        </div>
                    </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--form-gap, 15px)', width: '100%', marginTop: 'var(--form-gap, 15px)' }}>

                    {/* Time Tracking Widget */}
                    <div className="pagelet">
                        <div className="pagelet-head">
                            <h4><Icon icon={icons.clock} /> Current Shift</h4>
                        </div>
                        <div className="pagelet-in" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '280px' }}>
                            <div style={{ textAlign: 'center', marginBottom: '30px', width: '100%' }}>
                                <div style={{ fontSize: '14px', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '10px' }}>
                                    {isClockedOut ? 'Shift Complete' : isClockedIn ? 'Elapsed Time' : 'Ready to Start'}
                                </div>
                                <div style={{ fontSize: '48px', fontWeight: '800', fontVariantNumeric: 'tabular-nums', color: 'var(--text-color)' }}>
                                    {isClockedOut && todayStatus ? `${todayStatus.total_hours}h` : isClockedIn ? formatTime(elapsedSeconds) : '00:00:00'}
                                </div>
                            </div>

                            <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
                                <div style={{ width: '100%', maxWidth: '350px' }}>
                                    {!isClockedIn && !isClockedOut ? (
                                        <button
                                            className="button btn-primary"
                                            onClick={handleClockIn}
                                            disabled={loading}
                                            style={{ width: '100%', height: '56px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', borderRadius: '12px' }}
                                        >
                                            <FiPlay size={22} /> Start Shift
                                        </button>
                                    ) : isClockedIn ? (
                                        <button
                                            className="button btn-danger"
                                            onClick={handleClockOut}
                                            disabled={loading}
                                            style={{ width: '100%', height: '56px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', borderRadius: '12px' }}
                                        >
                                            <FiSquare size={22} /> End Shift
                                        </button>
                                    ) : (
                                        <div className="td-badge success" style={{ width: '100%', padding: '16px', fontSize: '15px', justifyContent: 'center', display: 'flex', alignItems: 'center', borderRadius: '12px' }}>
                                            <Icon icon={icons.checkCircle} style={{ marginRight: '8px', fontSize: '18px' }} />
                                            You have completed your shift for today.
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Timeline */}
                    <div className="pagelet">
                        <div className="pagelet-head">
                            <h4><Icon icon={icons.history} /> Timeline Activity</h4>
                        </div>
                        <div className="pagelet-in" style={{ height: '280px', padding: 0, display: 'flex', flexDirection: 'column' }}>
                            <div className="table-container" style={{ border: 'none', boxShadow: 'none', flex: 1, display: 'flex', flexDirection: 'column' }}>
                                <div className="table-body" style={{ minHeight: 'auto', marginBottom: 0, flex: 1, overflowY: 'auto' }}>
                                    <table className="table">
                                        <tbody className="tbody">
                                            {todayStatus && todayStatus.clock_out && (
                                                <tr className="tr">
                                                    <td className="td" style={{ width: '40px', paddingLeft: 'var(--pagelet-padding)' }}>
                                                        <span className="td-badge danger"><FiSquare size={14} /></span>
                                                    </td>
                                                    <td className="td">
                                                        <div className="tr-name"><b>Shift Ended</b></div>
                                                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>Duration: {todayStatus.total_hours}h</div>
                                                    </td>
                                                    <td className="td" style={{ textAlign: 'right', paddingRight: 'var(--pagelet-padding)' }}>
                                                        {new Date(todayStatus.clock_out).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                </tr>
                                            )}
                                            {todayStatus && todayStatus.clock_in && (
                                                <tr className="tr">
                                                    <td className="td" style={{ width: '40px', paddingLeft: 'var(--pagelet-padding)' }}>
                                                        <span className="td-badge success"><FiPlay size={14} /></span>
                                                    </td>
                                                    <td className="td">
                                                        <div className="tr-name"><b>Shift Started</b></div>
                                                        <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>System recorded</div>
                                                    </td>
                                                    <td className="td" style={{ textAlign: 'right', paddingRight: 'var(--pagelet-padding)' }}>
                                                        {new Date(todayStatus.clock_in).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                    </td>
                                                </tr>
                                            )}
                                            {(!todayStatus || (!todayStatus.clock_in && !todayStatus.clock_out)) && (
                                                <tr className="tr tr-no-records">
                                                    <td className="td" colSpan="3">No activity today.</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </div>
    );
};

export default EmployeeDashboard;
